import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { PDF_THEMES, DEFAULT_THEME } from '../themes';
import PdfHeader from '../components/PdfHeader';
import PdfFooter from '../components/PdfFooter';
import PdfSummaryCards from '../components/PdfSummaryCards';
import { gridCellStyle, gridRowStyle } from '../components/PdfTable';

const COLS = [
  { label: '#',       flex: 0.4, align: 'center' },
  { label: 'Name',    flex: 2 },
  { label: 'Phone',   flex: 1.5 },
  { label: 'Email',   flex: 2.5 },
  { label: 'Address', flex: 3 },
];

const s = StyleSheet.create({
  page:      { fontFamily: 'NotoSans', paddingBottom: 60 },
  content:   { marginHorizontal: 20, marginTop: 10 },
  ownerRow:  { paddingHorizontal: 6, paddingVertical: 5, marginTop: 6 },
  ownerText: { fontSize: 9, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 },
  headRow:   { flexDirection: 'row', paddingHorizontal: 4, paddingVertical: 4 },
  headCell:  { fontSize: 7, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.4 },
  dataRow:   { flexDirection: 'row', paddingHorizontal: 4, paddingVertical: 3, minHeight: 16 },
  cell:      { fontSize: 7.5, fontFamily: 'NotoSans', overflow: 'hidden' },
});

function buildAddress(p) {
  const parts = [
    p.flatNo && p.buildingName
      ? `${p.flatNo}, ${p.buildingName}`
      : (p.flatNo || p.buildingName),
    p.addressLine1,
    p.city,
  ].filter(Boolean);
  return parts.join(', ') || '—';
}

export default function MemberDirectoryDocument({ data, theme: themeKey, orientation, sections }) {
  const t      = PDF_THEMES[themeKey] || PDF_THEMES[DEFAULT_THEME];
  const groups = Array.isArray(data?.groups) ? data.groups : [];

  const totalOwners  = groups.length;
  const totalPersons = groups.reduce((sum, g) => sum + (g.persons?.length || 0), 0);

  return (
    <Document>
      <Page size="A4" orientation={orientation || 'portrait'} style={[s.page, { backgroundColor: t.white }]}>
        {sections.header && (
          <PdfHeader derasar={data.derasar} reportTitle="Member Directory" theme={t} />
        )}
        <View style={s.content}>
          {sections.summaryCards && (
            <PdfSummaryCards theme={t} items={[
              { label: 'Total Owners',  value: String(totalOwners)  },
              { label: 'Total Members', value: String(totalPersons) },
            ]} />
          )}

          {groups.map((group, gi) => (
            <View key={gi}>
              {/* Owner row */}
              <View style={[s.ownerRow, { backgroundColor: t.light }]} wrap={false}>
                <Text style={[s.ownerText, { color: t.body }]}>{group.ownerName}</Text>
              </View>

              {/* Column headers */}
              <View style={[s.headRow, { backgroundColor: t.dark }, gridRowStyle(t, { top: true, bottom: true })]} wrap={false}>
                {COLS.map((col, ci) => (
                  <Text
                    key={ci}
                    style={[s.headCell, { flex: col.flex, textAlign: col.align || 'left', color: t.headerText }, gridCellStyle(t, ci)]}
                  >
                    {col.label}
                  </Text>
                ))}
              </View>

              {/* Person rows */}
              {(group.persons || []).map((p, pi) => {
                const phone        = p.phone || group.ownerPhone;
                const email        = p.email || group.ownerEmail;
                const phoneFallback = !p.phone && !!group.ownerPhone;
                const emailFallback = !p.email && !!group.ownerEmail;
                return (
                <View
                  key={pi}
                  style={[s.dataRow, { backgroundColor: pi % 2 === 0 ? t.white : t.rowAlt }, gridRowStyle(t, { bottom: true })]}
                  wrap={false}
                >
                  <Text style={[s.cell, { flex: COLS[0].flex, textAlign: 'center', color: t.muted }, gridCellStyle(t, 0)]}>{pi + 1}</Text>
                  <Text style={[s.cell, { flex: COLS[1].flex, color: t.body }, gridCellStyle(t, 1)]}>{p.name || '—'}</Text>
                  <Text style={[s.cell, { flex: COLS[2].flex, color: phoneFallback ? t.muted : t.body }, gridCellStyle(t, 2)]}>{phone || '—'}</Text>
                  <Text style={[s.cell, { flex: COLS[3].flex, color: emailFallback ? t.muted : t.body }, gridCellStyle(t, 3)]}>{email || '—'}</Text>
                  <Text style={[s.cell, { flex: COLS[4].flex, color: t.body }, gridCellStyle(t, 4)]}>{buildAddress(p)}</Text>
                </View>
                );
              })}
            </View>
          ))}
        </View>
        {sections.footer && <PdfFooter theme={t} />}
      </Page>
    </Document>
  );
}
